export interface TagVisibilityBarProps {
  visible: number;
  hidden: number;
  needsReview: number;
  /** Overrides the generated aria-label — e.g. to name the matéria it belongs to. */
  ariaLabel?: string;
  className?: string;
}

type SegmentKey = "visible" | "hidden" | "needsReview";

// Same token family CertameEditar's TagCounterTile and visible-tag chips already
// use — `select` is "the selected treatment" shown on a visible tag chip, `warn`
// is the needs-review tone the counter tile and alert box already carry, and
// `edge-strong` is the project's existing neutral divider tone. No new color.
const SEGMENTS: { key: SegmentKey; label: string; fillClassName: string }[] = [
  { key: "visible", label: "Visíveis", fillClassName: "bg-select" },
  { key: "hidden", label: "Ocultas", fillClassName: "bg-edge-strong" },
  {
    key: "needsReview",
    label: "Precisam de revisão",
    fillClassName: "bg-warn",
  },
];

/**
 * Three proportional segments summarizing a tag set's visible/hidden/needs-review
 * split. Used both for the certame-wide totals and, smaller, inside each
 * matéria's own section header.
 */
export default function TagVisibilityBar({
  visible,
  hidden,
  needsReview,
  ariaLabel,
  className = "",
}: TagVisibilityBarProps) {
  const counts: Record<SegmentKey, number> = { visible, hidden, needsReview };
  const total = visible + hidden + needsReview;

  const summary =
    ariaLabel ??
    `${visible} visíveis, ${hidden} ocultas, ${needsReview} precisam de revisão`;

  return (
    <div
      role="img"
      aria-label={summary}
      className={`flex h-2 w-full overflow-hidden rounded-full bg-surface-muted ${className}`}
    >
      {total > 0 &&
        SEGMENTS.map(({ key, label, fillClassName }) => {
          const value = counts[key];
          if (!value) return null;
          const pct = (value / total) * 100;
          return (
            <div
              key={key}
              aria-hidden="true"
              title={`${label}: ${value}`}
              style={{ width: `${pct}%` }}
              className={fillClassName}
            />
          );
        })}
    </div>
  );
}
